import * as React from 'react'
import {
  Command as CommandPrimitive,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from 'cmdk'
import { cn } from '../../lib/utils'

const Command = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive>
>(({ className, ...props }, ref) => (
  <CommandPrimitive
    ref={ref}
    className={cn(
      'flex h-full w-full flex-col overflow-hidden rounded-md bg-stone-900 text-stone-100',
      className
    )}
    {...props}
  />
))
Command.displayName = CommandPrimitive.displayName

const CommandInputStyled = React.forwardRef<
  React.ElementRef<typeof CommandInput>,
  React.ComponentPropsWithoutRef<typeof CommandInput>
>(({ className, ...props }, ref) => (
  <div className="flex items-center border-b border-stone-700 px-3">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="mr-2 h-4 w-4 shrink-0 text-stone-500"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </svg>
    <CommandInput
      ref={ref}
      className={cn(
        'flex h-10 w-full rounded-md bg-transparent py-3 text-sm text-stone-100 outline-none placeholder:text-stone-500 disabled:cursor-not-allowed disabled:opacity-50',
        className
      )}
      {...props}
    />
  </div>
))
CommandInputStyled.displayName = 'CommandInputStyled'

const CommandListStyled = React.forwardRef<
  React.ElementRef<typeof CommandList>,
  React.ComponentPropsWithoutRef<typeof CommandList>
>(({ className, ...props }, ref) => (
  <CommandList
    ref={ref}
    className={cn('max-h-[260px] overflow-y-auto overflow-x-hidden', className)}
    {...props}
  />
))
CommandListStyled.displayName = 'CommandListStyled'

const CommandEmptyStyled = React.forwardRef<
  React.ElementRef<typeof CommandEmpty>,
  React.ComponentPropsWithoutRef<typeof CommandEmpty>
>((props, ref) => (
  <CommandEmpty ref={ref} className="py-6 text-center text-sm text-stone-500" {...props} />
))
CommandEmptyStyled.displayName = 'CommandEmptyStyled'

const CommandGroupStyled = React.forwardRef<
  React.ElementRef<typeof CommandGroup>,
  React.ComponentPropsWithoutRef<typeof CommandGroup>
>(({ className, ...props }, ref) => (
  <CommandGroup
    ref={ref}
    className={cn(
      'overflow-hidden p-1 text-stone-100 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-stone-500',
      className
    )}
    {...props}
  />
))
CommandGroupStyled.displayName = 'CommandGroupStyled'

const CommandItemStyled = React.forwardRef<
  React.ElementRef<typeof CommandItem>,
  React.ComponentPropsWithoutRef<typeof CommandItem>
>(({ className, ...props }, ref) => (
  <CommandItem
    ref={ref}
    className={cn(
      "relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none data-[disabled=true]:pointer-events-none data-[selected='true']:bg-stone-700 data-[selected='true']:text-stone-100 data-[disabled=true]:opacity-50",
      className
    )}
    {...props}
  />
))
CommandItemStyled.displayName = 'CommandItemStyled'

const CommandSeparatorStyled = React.forwardRef<
  React.ElementRef<typeof CommandSeparator>,
  React.ComponentPropsWithoutRef<typeof CommandSeparator>
>(({ className, ...props }, ref) => (
  <CommandSeparator ref={ref} className={cn('-mx-1 h-px bg-stone-700', className)} {...props} />
))
CommandSeparatorStyled.displayName = 'CommandSeparatorStyled'

export {
  Command,
  CommandInputStyled as CommandInput,
  CommandListStyled as CommandList,
  CommandEmptyStyled as CommandEmpty,
  CommandGroupStyled as CommandGroup,
  CommandItemStyled as CommandItem,
  CommandSeparatorStyled as CommandSeparator,
}
